import { showErrorToast } from "../../../../../components/common/ShowToast";
import { ErrorComment } from "./processExcelComment";

export interface FileComment {
  id: number;
  first_name: string;
  last_name: string;
  comment: string;
  phone_number: string;
  email: string;
}

export interface ErrorRow {
  id: number;
  data: string[];
}

export const processCSVFile = async (
  file: File,
  stream_id: string,
  fieldsMapped: {
    last_name: string;
    first_name: string;
    email: string;
    phone_number: string;
    comment: string;
  }
): Promise<{
  message: string;
  errorOccured: boolean;
  comments: FileComment[];
  errorComments: ErrorComment[];
}> => {
  try {
    const rawData = await readFileAsText(file);
    const headings = await processHeadings(file);
    const correctCommentLineHeadings = headings.length; // heading is number of colums e.g if a line has 3 colums instead of 4 it is flagged as an error comment
    const fieldsMappedKeys = Object.keys(
      fieldsMapped
    ) as (keyof typeof fieldsMapped)[];
    const fieldsIndexMap: {
      field: string;
      mappedString: string;
      index: number;
    }[] = [];

    for (let i = 0; i < fieldsMappedKeys.length; i++) {
      const currentKey = fieldsMappedKeys[i];
      const currentValue = fieldsMapped[currentKey];
      const headingIndexInFile = headings.indexOf(currentValue);
      fieldsIndexMap.unshift({
        field: currentKey,
        mappedString: currentValue,
        index: headingIndexInFile,
      });
    }

    // Preparing the comments and separating them
    const rows = rawData.trim().split("\n");
    const parsedRows = rows.map((row) => row.split(","));
    const commentsRows = parsedRows.filter(
      (cols) => cols.length === correctCommentLineHeadings
    );
    const errorRows = parsedRows.filter(
      (cols) => cols.length !== correctCommentLineHeadings
    );

    const errorCommentsMapped: ErrorComment[] = errorRows.map(
      (errorComment, index) => {
        const extractedIndex = extractIndexs(fieldsIndexMap);

        if (errorComment.length > correctCommentLineHeadings) {
          const unknownFieldsArray = errorComment.slice(
            correctCommentLineHeadings
          );
          return {
            id: index,
            first_name: errorComment[extractedIndex.firstNameFileIndex],
            last_name: errorComment[extractedIndex.lastNameFileIndex],
            comment: errorComment[extractedIndex.commentFileIndex],
            email: errorComment[extractedIndex.emailFileIndex],
            phone_number: errorComment[extractedIndex.phoneNumberFileIndex],
            unknownFieldsArray: [...unknownFieldsArray],
          };
        }

        return {
          id: index,
          first_name: errorComment[extractedIndex.firstNameFileIndex],
          last_name: errorComment[extractedIndex.lastNameFileIndex],
          comment: errorComment[extractedIndex.commentFileIndex],
          email: errorComment[extractedIndex.emailFileIndex],
          phone_number: errorComment[extractedIndex.phoneNumberFileIndex],
          unknownFieldsArray: undefined,
        };
      }
    );

    // Parsing the comments into usable data type
    const comments: FileComment[] = commentsRows.map((cols, index) => {
      const firstNameFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "first_name"
      )[0].index;
      const lastNameFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "last_name"
      )[0].index;
      const commentFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "comment"
      )[0].index;
      const emailFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "email"
      )[0].index;
      const phoneNumberFileIndex = fieldsIndexMap.filter(
        (obj) => obj.field == "phone_number"
      )[0].index;
      return {
        id: index,
        first_name: cols[firstNameFileIndex],
        last_name: cols[lastNameFileIndex],
        comment: cols[commentFileIndex],
        email: cols[emailFileIndex],
        phone_number: cols[phoneNumberFileIndex],
      };
    });

    comments.shift(); // remove the heading of the file

    return {
      message: "File processed successfully",
      errorOccured: false,
      comments,
      errorComments: errorCommentsMapped,
    };
  } catch (err) {
    return {
      message:
        typeof err === "string"
          ? err
          : "Failed to process the file. Please make sure it is not corrupted.",
      errorOccured: true,
      comments: [],
      errorComments: [],
    };
  }
};

export const processHeadings = async (file: File) => {
  const rawData = await readFileAsText(file);
  const rows = rawData.trim().split("\n");
  const parsedRows = rows.map((row) => row.split(","));
  return parsedRows[0];
};

const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      if (result) {
        resolve(result.toString());
      } else {
        reject("Failed to read file");
      }
    };

    reader.onerror = () => reject("Error while reading file");

    reader.readAsText(file);
  });
};

const extractIndexs = (
  fieldsIndexMap: {
    field: string;
    mappedString: string;
    index: number;
  }[]
): {
  firstNameFileIndex: number;
  lastNameFileIndex: number;
  commentFileIndex: number;
  emailFileIndex: number;
  phoneNumberFileIndex: number;
} => {
  const firstNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "first_name"
  )[0].index;
  const lastNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "last_name"
  )[0].index;
  const commentFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "comment"
  )[0].index;
  const emailFileIndex = fieldsIndexMap.filter((obj) => obj.field == "email")[0]
    .index;
  const phoneNumberFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field == "phone_number"
  )[0].index;

  return {
    firstNameFileIndex,
    lastNameFileIndex,
    commentFileIndex,
    emailFileIndex,
    phoneNumberFileIndex,
  };
};
